import { Button, DatePicker, Input, InputNumber, Space, Switch } from "antd";
import { ICalendarEvent } from "@blings/blings-player";
import dayjs, { Dayjs } from "dayjs";
import { useEffect, useState } from "react";
import { ExpressionIcon } from "../../../../icons-and-animations/Icons";
import {
  formatCalendarPickerDateTime,
  getCalendarEndMode,
  getCalendarPickerDateTime,
  getExclusiveAllDayEnd,
  getInclusiveAllDayEnd,
  setCalendarAllDay,
  setCalendarEndMode,
  updateCalendarEventField,
} from "../../calendarConnector";

const FieldLabel = ({
  children,
  required,
}: {
  children: React.ReactNode;
  required?: boolean;
}) => (
  <span className="calendar-field-label">
    {children}
    {required ? " *" : ""}
  </span>
);

const getPickerValue = (value?: string): Dayjs | null => {
  if (!value) return null;
  const parsed = dayjs(getCalendarPickerDateTime(value));
  return parsed.isValid() ? parsed : null;
};

const BinaryChoiceToggle = ({
  leftLabel,
  rightLabel,
  checked,
  onChange,
  ariaLabel,
}: {
  leftLabel: string;
  rightLabel: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  ariaLabel: string;
}) => (
  <Space align="center" size={16}>
    <span>{leftLabel}</span>
    <Switch aria-label={ariaLabel} checked={checked} onChange={onChange} />
    <span>{rightLabel}</span>
  </Space>
);

export const CalendarEventEditor = ({
  value,
  onChange,
  onOpenCode,
}: {
  value?: ICalendarEvent;
  onChange: (value: ICalendarEvent) => void;
  onOpenCode?: () => void;
}) => {
  const event =
    value && typeof value === "object" ? value : ({} as ICalendarEvent);
  const allDay = event.allDay === true;
  const [endMode, setEndMode] = useState(() => getCalendarEndMode(event));
  const update = (key: keyof ICalendarEvent, fieldValue: unknown) =>
    onChange(updateCalendarEventField(event, key, fieldValue));

  useEffect(() => {
    if (typeof event.durationMinutes !== "undefined") {
      setEndMode("duration");
    } else if (typeof event.end !== "undefined") {
      setEndMode("end");
    }
  }, [event.durationMinutes, event.end]);

  const handleEndModeChange = (mode: "end" | "duration") => {
    setEndMode(mode);
    onChange(setCalendarEndMode(event, mode));
  };

  return (
    <Space direction="vertical" className="calendar-event-editor" size={12}>
      <div>
        <FieldLabel required>Event title</FieldLabel>
        <div className="calendar-title-input-row">
          <Input
            size="large"
            aria-label="Event title"
            value={event.title}
            placeholder="Product launch"
            onChange={(inputEvent) => update("title", inputEvent.target.value)}
            onBlur={(inputEvent) =>
              update("title", inputEvent.target.value.trim())
            }
            allowClear
          />
          {onOpenCode && (
            <Button
              aria-label="Use calendar code"
              title="Use calendar code"
              className="open-code-icon"
              style={{
                display: "flex",
                alignItems: "center",
              }}
              onClick={onOpenCode}
            >
              <ExpressionIcon />
            </Button>
          )}
        </div>
      </div>

      <BinaryChoiceToggle
        leftLabel="Timed event"
        rightLabel="All-day event"
        ariaLabel="Event timing"
        checked={allDay}
        onChange={(checked) => onChange(setCalendarAllDay(event, checked))}
      />

      <label>
        <FieldLabel required>Starts</FieldLabel>
        <DatePicker
          size="large"
          value={getPickerValue(event.start)}
          format={allDay ? "YYYY-MM-DD" : "YYYY-MM-DD HH:mm"}
          showTime={allDay ? false : { format: "HH:mm" }}
          showNow={false}
          needConfirm={false}
          placeholder={
            allDay ? "Select start date" : "Select start date and time"
          }
          onChange={(date) =>
            update(
              "start",
              date &&
                (allDay
                  ? date.format("YYYY-MM-DD")
                  : formatCalendarPickerDateTime(
                      date.format("YYYY-MM-DDTHH:mm"),
                      event.start
                    ))
            )
          }
          allowClear
        />
      </label>

      {allDay ? (
        <label>
          <FieldLabel>Last day</FieldLabel>
          <DatePicker
            size="large"
            value={getPickerValue(getInclusiveAllDayEnd(event.end))}
            format="YYYY-MM-DD"
            placeholder="Select last day"
            disabledDate={(date) =>
              Boolean(
                getPickerValue(event.start) &&
                  date.isBefore(getPickerValue(event.start), "day")
              )
            }
            onChange={(date) =>
              update("end", getExclusiveAllDayEnd(date?.format("YYYY-MM-DD")))
            }
            allowClear
          />
          <span className="calendar-field-help">
            Leave empty for a one-day event.
          </span>
        </label>
      ) : (
        <>
          <div>
            <FieldLabel required>Ends</FieldLabel>
            <BinaryChoiceToggle
              leftLabel="At date/time"
              rightLabel="After duration"
              ariaLabel="Event end mode"
              checked={endMode === "duration"}
              onChange={(checked) =>
                handleEndModeChange(checked ? "duration" : "end")
              }
            />
          </div>

          {endMode === "end" ? (
            <label>
              <FieldLabel required>End date and time</FieldLabel>
              <DatePicker
                size="large"
                value={getPickerValue(event.end)}
                format="YYYY-MM-DD HH:mm"
                showTime={{ format: "HH:mm" }}
                showNow={false}
                needConfirm={false}
                placeholder="Select end date and time"
                disabledDate={(date) =>
                  Boolean(
                    getPickerValue(event.start) &&
                      date.isBefore(getPickerValue(event.start), "day")
                  )
                }
                onChange={(date) =>
                  update(
                    "end",
                    date &&
                      formatCalendarPickerDateTime(
                        date.format("YYYY-MM-DDTHH:mm"),
                        event.end
                      )
                  )
                }
                allowClear
              />
            </label>
          ) : (
            <label>
              <FieldLabel required>Duration (minutes)</FieldLabel>
              <InputNumber
                size="large"
                min={1}
                precision={0}
                value={event.durationMinutes}
                placeholder="30"
                onChange={(minutes) =>
                  update("durationMinutes", minutes ?? undefined)
                }
              />
            </label>
          )}
        </>
      )}

      <label>
        <FieldLabel>Location</FieldLabel>
        <Input
          size="large"
          value={event.location}
          placeholder="Event location"
          onChange={(inputEvent) => update("location", inputEvent.target.value)}
          allowClear
        />
      </label>

      <label>
        <FieldLabel>Description</FieldLabel>
        <Input.TextArea
          rows={3}
          value={event.description}
          placeholder="Add details for the viewer"
          onChange={(inputEvent) =>
            update("description", inputEvent.target.value)
          }
          allowClear
        />
      </label>

      <label>
        <FieldLabel>Event URL</FieldLabel>
        <Input
          size="large"
          value={event.url}
          placeholder="https://example.com/event"
          onChange={(inputEvent) => update("url", inputEvent.target.value)}
          allowClear
        />
      </label>

      <label>
        <FieldLabel>Reminder (minutes before)</FieldLabel>
        <InputNumber
          size="large"
          min={0}
          precision={0}
          value={event.reminderMinutes}
          placeholder="15"
          onChange={(minutes) =>
            update("reminderMinutes", minutes ?? undefined)
          }
        />
        <span className="calendar-field-help">
          Custom reminders are included in .ics events only.
        </span>
      </label>

      <label>
        <FieldLabel>Event UID (advanced)</FieldLabel>
        <Input
          size="large"
          value={event.uid}
          placeholder="event-123@example.com"
          onChange={(inputEvent) => update("uid", inputEvent.target.value)}
          allowClear
        />
        <span className="calendar-field-help">
          Leave empty to let the Player generate a stable UID.
        </span>
      </label>
    </Space>
  );
};
